import React, { useContext, useEffect } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import OptionSwitch from "./OptionSwitch"
import Select from "react-select"
import { DOWNLOAD_TYPES, INPUTS } from "hooks/budgeting/useDownloadBudgetConfig"
import { Badge, Tooltip } from "@mui/material"
import {
  optionBoxStyle,
  optionSelectStyle,
  optionSelectWrapper,
} from "components/Documents/StylesAndUtils/CustomStyles"
import SubscriptionContext from "context/subscription/subscriptionContext"
import useEntitlementGuard from "hooks/useEntitlementGuard"
import {
  EXPORT_PDF_INFO,
  EXPORT_PDF_GENERALS,
  EXPORT_PDF_DECOMPOSITION,
  EXPORT_PDF_COVER,
  EXPORT_PDF_SIGNATURE,
  EXPORT_PDF_DATE,
  EXPORT_PDF_NEW_FORMAT,
  GRANTED,
  EXPORT_PDF_SUMMARY,
} from "constants/entitlements"

const PdfOptions = ({
  setDownloadBudgetConfig,
  clientPriceRef,
  clientPrice,
  setClientPrice,
  companyAndCustomerInfo,
  setCompanyAndCustomerInfo,
  generalConditions,
  setGeneralConditions,
  decomposition,
  setDecomposition,
  briefingPosition,
  setBriefingPosition,
  briefingPositionRef,
  addCover,
  setAddCover,
  addSignature,
  setAddSignature,
  addDate,
  setAddDate,
  newFormat,
  setNewFormat,
}) => {
  const tipo = DOWNLOAD_TYPES.PDF
  const { subscriptionState } = useContext(SubscriptionContext)
  const [checkEntitlement, entitlementDialog] = useEntitlementGuard()

  const handleEntitlementCheck = (permission, state, setState, inputKey) => {
    const hasPermission =
      permission === GRANTED ||
      subscriptionState?.subscription_entitlements?.some(
        ent => ent.feature_name === permission && ent.is_enabled === true,
      )

    const handleBlocked = () => {
      checkEntitlement(permission, i18n.t(`downloadBudget.${inputKey}`))
    }

    useEffect(() => {
      if (!hasPermission && state) {
        setState(false)
        setDownloadBudgetConfig(tipo, INPUTS[inputKey], false)
      }
    }, [hasPermission, state, setState, setDownloadBudgetConfig, tipo])

    return hasPermission ? null : handleBlocked
  }

  const handleExportPdfInfoBlocked = handleEntitlementCheck(
    EXPORT_PDF_INFO,
    companyAndCustomerInfo,
    setCompanyAndCustomerInfo,
    "companyAndClientInfo",
  )

  const handleExportPdfGeneralsBlocked = handleEntitlementCheck(
    EXPORT_PDF_GENERALS,
    generalConditions,
    setGeneralConditions,
    "generalConditions",
  )

  const handleExportPdfDecompositionBlocked = handleEntitlementCheck(
    EXPORT_PDF_DECOMPOSITION,
    decomposition,
    setDecomposition,
    "decomposition",
  )

  const handleExportPdfCoverBlocked = handleEntitlementCheck(
    EXPORT_PDF_COVER,
    addCover,
    setAddCover,
    "addCover",
  )

  const handleExportPdfSignatureBlocked = handleEntitlementCheck(
    EXPORT_PDF_SIGNATURE,
    addSignature,
    setAddSignature,
    "addSignature",
  )

  const handleExportPdfDateBlocked = handleEntitlementCheck(
    EXPORT_PDF_DATE,
    addDate,
    setAddDate,
    "addDate",
  )

  const handleExportPdfNewFormatBlocked = handleEntitlementCheck(
    EXPORT_PDF_NEW_FORMAT,
    newFormat,
    setNewFormat,
    "newFormat",
  )

  const selectPriceOptions = [
    { value: "client", label: i18n.t("downloadBudget.clientPrice") },
    { value: "blind", label: i18n.t("downloadBudget.noPrice") },
    { value: "cost", label: i18n.t("downloadBudget.targetCost") },
    { value: "costReal", label: i18n.t("downloadBudget.actualCost") },
  ]

  const briefingPositionOptions = [
    { value: false, label: i18n.t("downloadBudget.noSummary") },
    { value: "top", label: i18n.t("downloadBudget.summaryAtStart") },
    { value: "bottom", label: i18n.t("downloadBudget.summaryAtEnd") },
  ]

  const handleBriefingPositionClick = e => {
    const hasPermission =
      EXPORT_PDF_SUMMARY === GRANTED ||
      subscriptionState?.subscription_entitlements?.some(
        ent =>
          ent.feature_name === EXPORT_PDF_SUMMARY && ent.is_enabled === true,
      )

    if (!hasPermission) {
      checkEntitlement(EXPORT_PDF_SUMMARY, i18n.t("downloadBudget.noSummary"))
      setBriefingPosition(false)
      setDownloadBudgetConfig(tipo, INPUTS.BRIEFING_POSITION, false)
    } else {
      setBriefingPosition(e.value)
      setDownloadBudgetConfig(tipo, INPUTS.BRIEFING_POSITION, e.value)
    }
  }

  const pdfFirstSwitches = [
    {
      i18nKey: "downloadBudget.companyAndClientInfo",
      state: companyAndCustomerInfo,
      setState: setCompanyAndCustomerInfo,
      key: `pdf_${INPUTS.COMPANY_AND_CLIENT_INFO}`,
      inputKey: INPUTS.COMPANY_AND_CLIENT_INFO,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfInfoBlocked,
    },
    {
      i18nKey: "downloadBudget.generalConditions",
      state: generalConditions,
      setState: setGeneralConditions,
      key: `pdf_${INPUTS.GENERAL_CONDITIONS}`,
      inputKey: INPUTS.GENERAL_CONDITIONS,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfGeneralsBlocked,
    },
    {
      i18nKey: "downloadBudget.decomposition",
      state: decomposition,
      setState: setDecomposition,
      key: `pdf_${INPUTS.PART_DECOMPOSITION}`,
      inputKey: INPUTS.PART_DECOMPOSITION,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfDecompositionBlocked,
    },
  ]

  const pdfSecondSwitches = [
    {
      i18nKey: "downloadBudget.addCover",
      state: addCover,
      setState: setAddCover,
      key: `pdf_${INPUTS.ADD_COVER}`,
      inputKey: INPUTS.ADD_COVER,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfCoverBlocked,
    },
    {
      i18nKey: "downloadBudget.addSignature",
      state: addSignature,
      setState: setAddSignature,
      key: `pdf_${INPUTS.ADD_SIGNATURE}`,
      inputKey: INPUTS.ADD_SIGNATURE,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfSignatureBlocked,
    },
    {
      i18nKey: "downloadBudget.addDate",
      state: addDate,
      setState: setAddDate,
      key: `pdf_${INPUTS.ADD_DATE}`,
      inputKey: INPUTS.ADD_DATE,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfDateBlocked,
    },
    {
      i18nKey: "downloadBudget.newFormat",
      state: newFormat,
      setState: setNewFormat,
      key: `pdf_${INPUTS.NEW_FORMAT}`,
      inputKey: INPUTS.NEW_FORMAT,
      tipo: tipo,
      setDownloadBudgetConfig: setDownloadBudgetConfig,
      onToggleBlocked: handleExportPdfNewFormatBlocked,
    },
  ]

  return (
    <>
      <div style={optionSelectWrapper}>
        <Select
          ref={clientPriceRef}
          options={selectPriceOptions}
          styles={optionSelectStyle}
          value={
            clientPrice
              ? selectPriceOptions.find(o => o.value === clientPrice)
              : selectPriceOptions[0]
          }
          onChange={e => {
            setClientPrice(e.value)
            setDownloadBudgetConfig(tipo, INPUTS.PDF_PRICE_OPTION, e.value)
            clientPriceRef.current.blur()
          }}
        />
      </div>

      {pdfFirstSwitches.map(opt => (
        <OptionSwitch key={opt.key} {...opt} />
      ))}

      <div style={optionSelectWrapper}>
        <Select
          ref={briefingPositionRef}
          options={briefingPositionOptions}
          styles={optionSelectStyle}
          value={
            briefingPosition
              ? briefingPositionOptions.find(o => o.value === briefingPosition)
              : briefingPositionOptions[0]
          }
          onChange={handleBriefingPositionClick}
        />
      </div>

      {pdfSecondSwitches.map(opt => (
        <OptionSwitch key={opt.key} {...opt} />
      ))}

      {!newFormat && (
        <div id="Poppinsnormal" style={optionBoxStyle}>
          <div>
            <Tooltip title="">
              <Badge
                color="secondary"
                badgeContent="!"
                style={{ marginLeft: "5px" }}
              />
            </Tooltip>
          </div>
          <div
            style={{
              marginLeft: "20px",
              fontFamily: "Helvetica",
            }}
          >
            {i18n.t("downloadBudget.documentLanguageNotice")}
          </div>
        </div>
      )}
      {entitlementDialog}
    </>
  )
}

PdfOptions.propTypes = {
  setDownloadBudgetConfig: PropTypes.func.isRequired,
  clientPriceRef: PropTypes.any.isRequired,
  clientPrice: PropTypes.string.isRequired,
  setClientPrice: PropTypes.func.isRequired,
  companyAndCustomerInfo: PropTypes.bool.isRequired,
  setCompanyAndCustomerInfo: PropTypes.func.isRequired,
  generalConditions: PropTypes.bool.isRequired,
  setGeneralConditions: PropTypes.func.isRequired,
  decomposition: PropTypes.bool.isRequired,
  setDecomposition: PropTypes.func.isRequired,
  briefingPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
    .isRequired,
  setBriefingPosition: PropTypes.func.isRequired,
  briefingPositionRef: PropTypes.any.isRequired,
  addCover: PropTypes.bool.isRequired,
  setAddCover: PropTypes.func.isRequired,
  addSignature: PropTypes.bool.isRequired,
  setAddSignature: PropTypes.func.isRequired,
  addDate: PropTypes.bool.isRequired,
  setAddDate: PropTypes.func.isRequired,
  newFormat: PropTypes.bool.isRequired,
  setNewFormat: PropTypes.func.isRequired,
}
export default PdfOptions
